<template>
  <div class="create-meeting-page">
    <!-- Page Header -->
    <div class="d-flex align-center mb-6">
      <v-btn
        icon
        variant="text"
        @click="$router.go(-1)"
        class="mr-4"
      >
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <div>
        <h1 class="text-h4 font-weight-bold text-primary">
          Schedule New Meeting
        </h1>
        <p class="text-subtitle-1 text-medium-emphasis">
          Create a new board meeting with agenda and attendees
        </p>
      </div>
    </div>

    <v-form ref="meetingForm" v-model="formValid" @submit.prevent="saveMeeting">
      <v-row>
        <!-- Main Form -->
        <v-col cols="12" md="8">
          <v-card class="mb-6">
            <v-card-title class="text-h6 font-weight-medium">
              Meeting Details
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="meeting.title"
                    label="Meeting Title *"
                    :rules="titleRules"
                    variant="outlined"
                    prepend-inner-icon="mdi-text"
                    placeholder="e.g., Monthly Board Meeting"
                    required
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-select
                    v-model="meeting.meeting_type_id"
                    :items="meetingTypes"
                    item-title="name"
                    item-value="id"
                    label="Meeting Type *"
                    :rules="typeRules"
                    variant="outlined"
                    prepend-inner-icon="mdi-shape"
                    required
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="meeting.location"
                    label="Location"
                    variant="outlined"
                    prepend-inner-icon="mdi-map-marker"
                    placeholder="e.g., Conference Room A, Virtual"
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="meeting.date"
                    label="Meeting Date *"
                    type="date"
                    :rules="dateRules"
                    variant="outlined"
                    prepend-inner-icon="mdi-calendar"
                    required
                  />
                </v-col>

                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="meeting.start_time"
                    label="Start Time *"
                    type="time"
                    :rules="timeRules"
                    variant="outlined"
                    prepend-inner-icon="mdi-clock"
                    required
                  />
                </v-col>

                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="meeting.end_time"
                    label="End Time"
                    type="time"
                    variant="outlined"
                    prepend-inner-icon="mdi-clock"
                  />
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="meeting.description"
                    label="Description"
                    variant="outlined"
                    prepend-inner-icon="mdi-text-long"
                    placeholder="Brief description of the meeting purpose and objectives"
                    rows="3"
                    auto-grow
                  />
                </v-col>

                <v-col cols="12">
                  <v-text-field
                    v-model="meeting.virtual_link"
                    label="Virtual Meeting Link"
                    variant="outlined"
                    prepend-inner-icon="mdi-video"
                    placeholder="e.g., https://zoom.us/j/123456789, https://teams.microsoft.com/..."
                    hint="Optional: Add a link for virtual/online meeting access"
                    persistent-hint
                  />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Agenda Section -->
          <v-card class="mb-6">
            <v-card-title class="d-flex justify-space-between align-center">
              <span class="text-h6 font-weight-medium">Meeting Agenda</span>
              <v-btn
                color="primary"
                variant="outlined"
                size="small"
                @click="addAgendaItem"
                prepend-icon="mdi-plus"
              >
                Add Item
              </v-btn>
            </v-card-title>
            <v-card-text>
              <div v-if="meeting.agenda_items.length === 0" class="text-center py-8">
                <v-icon size="48" color="grey" class="mb-2">
                  mdi-format-list-bulleted
                </v-icon>
                <p class="text-medium-emphasis">
                  No agenda items yet. Add items to structure your meeting.
                </p>
              </div>

              <div v-else class="agenda-list">
                <div
                  v-for="(item, index) in meeting.agenda_items"
                  :key="item.id"
                  class="agenda-item mb-4"
                >
                  <div class="d-flex align-center mb-2">
                    <span class="text-caption bg-primary px-2 py-1 rounded mr-3 text-white">
                      {{ index + 1 }}
                    </span>
                    <v-spacer />
                    <v-btn
                      icon
                      size="small"
                      variant="text"
                      color="error"
                      @click="removeAgendaItem(index)"
                    >
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </div>
                  
                  <v-row no-gutters>
                    <v-col cols="12" md="8">
                      <v-text-field
                        v-model="item.title"
                        placeholder="Agenda item title"
                        variant="outlined"
                        density="compact"
                        hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="4" class="pl-md-2">
                      <v-text-field
                        v-model="item.duration"
                        placeholder="Duration (min)"
                        type="number"
                        variant="outlined"
                        density="compact"
                        hide-details
                      />
                    </v-col>
                  </v-row>
                  
                  <v-textarea
                    v-model="item.description"
                    placeholder="Additional details (optional)"
                    variant="outlined"
                    density="compact"
                    rows="2"
                    auto-grow
                    hide-details
                    class="mt-2"
                  />
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Sidebar -->
        <v-col cols="12" md="4">
          <!-- Meeting Settings -->
          <v-card class="mb-6">
            <v-card-title class="text-h6 font-weight-medium">
              Meeting Settings
            </v-card-title>
            <v-card-text>
              <v-switch
                v-model="meeting.send_notifications"
                label="Send Email Notifications"
                color="primary"
                hide-details
                class="mb-4"
              />

              <v-switch
                v-model="meeting.is_private"
                label="Private Meeting"
                color="primary"
                hide-details
              />
            </v-card-text>
          </v-card>

          <!-- Attendees -->
          <v-card class="mb-6">
            <v-card-title class="d-flex justify-space-between align-center">
              <span class="text-h6 font-weight-medium">Attendees</span>
              <v-chip size="small" color="primary">
                {{ meeting.attendees.length }}
              </v-chip>
            </v-card-title>
            <v-card-text>
              <v-select
                v-model="meeting.attendees"
                :items="boardMembers"
                item-title="full_name"
                item-value="id"
                label="Select attendees"
                variant="outlined"
                density="compact"
                multiple
                chips
                closable-chips
                prepend-inner-icon="mdi-account-multiple"
              />
            </v-card-text>
          </v-card>

          <!-- Action Buttons -->
          <v-card>
            <v-card-text>
              <div class="d-flex flex-column gap-2">
                <v-btn
                  type="submit"
                  color="primary"
                  size="large"
                  block
                  :loading="saving"
                  :disabled="!formValid"
                >
                  Schedule Meeting
                </v-btn>

                <v-btn
                  variant="outlined"
                  size="large"
                  block
                  @click="saveDraft"
                  :disabled="saving"
                >
                  Save as Draft
                </v-btn>

                <v-btn
                  variant="text"
                  size="large"
                  block
                  @click="cancelCreation"
                >
                  Cancel
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-form>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useNotifications } from '@/composables/useNotifications'
import { dateFormat } from '@/utils/dateFormat'
import api from '@/services/api'

export default {
  name: 'CreateMeeting',
  setup() {
    const router = useRouter()
    const { 
      showMeetingNotification, 
      showOperationError, 
      showValidationErrors 
    } = useNotifications()

    // Form state
    const meetingForm = ref(null)
    const formValid = ref(false)
    const saving = ref(false)

    // Meeting data
    const meeting = reactive({
      title: '',
      description: '',
      meeting_type_id: null,
      date: '',
      start_time: '',
      end_time: '',
      location: '',
      virtual_link: '',
      send_notifications: true,
      is_private: false,
      attendees: [],
      agenda_items: []
    })

    // Reference data
    const meetingTypes = ref([])
    const boardMembers = ref([])

    // Form validation rules
    const titleRules = [
      v => !!v || 'Meeting title is required',
      v => v.length >= 3 || 'Title must be at least 3 characters'
    ]

    const typeRules = [
      v => !!v || 'Meeting type is required'
    ]

    const dateRules = [
      v => !!v || 'Meeting date is required',
      v => new Date(v) >= new Date().setHours(0,0,0,0) || 'Date cannot be in the past'
    ]

    const timeRules = [
      v => !!v || 'Start time is required'
    ]

    // Methods
    const addAgendaItem = () => {
      meeting.agenda_items.push({
        id: Date.now(),
        title: '',
        description: '',
        duration: ''
      })
    }

    const removeAgendaItem = (index) => {
      meeting.agenda_items.splice(index, 1)
    }

    const saveMeeting = async () => {
      if (!formValid.value) return

      saving.value = true
      try {
        // Calculate end time if not provided (default to 1 hour)
        let endTime = meeting.end_time
        if (!endTime && meeting.start_time) {
          const [hours, minutes] = meeting.start_time.split(':')
          const endHour = (parseInt(hours) + 1).toString().padStart(2, '0')
          endTime = `${endHour}:${minutes}`
        }

        const meetingData = {
          title: meeting.title,
          description: meeting.description || '',
          meeting_type_id: meeting.meeting_type_id,
          start_datetime: `${meeting.date}T${meeting.start_time}:00`,
          end_datetime: `${meeting.date}T${endTime}:00`,
          location: meeting.location || '',
          virtual_link: meeting.virtual_link || '',
          agenda: meeting.agenda_items.map(item => `${item.title}: ${item.description}`).join('\n') || '',
          status: 'scheduled'
        }

        console.log('Sending meeting data:', meetingData)
        const response = await api.post('/meetings/', meetingData)
        
        showMeetingNotification(
          'Scheduled', 
          meeting.title,
          dateFormat.formatDateTime(meetingData.start_datetime)
        )
        
        router.push(`/meetings/${response.data.meeting.id}`)
      } catch (error) {
        console.error('Error saving meeting:', error)
        if (error.response?.data?.errors) {
          showValidationErrors(error.response.data.errors)
        } else {
          showOperationError('schedule meeting', error)
        }
      } finally {
        saving.value = false
      }
    }

    const saveDraft = async () => {
      saving.value = true
      try {
        // For drafts, we need at least title and meeting_type_id
        if (!meeting.title || !meeting.meeting_type_id) {
          showValidationErrors(['Title and meeting type are required for draft'])
          return
        }

        // Calculate end time if not provided (default to 1 hour)
        let endTime = meeting.end_time
        if (!endTime && meeting.start_time) {
          const [hours, minutes] = meeting.start_time.split(':')
          const endHour = (parseInt(hours) + 1).toString().padStart(2, '0')
          endTime = `${endHour}:${minutes}`
        }

        const meetingData = {
          title: meeting.title,
          description: meeting.description || '',
          meeting_type_id: meeting.meeting_type_id,
          start_datetime: meeting.date && meeting.start_time ? `${meeting.date}T${meeting.start_time}:00` : `${meeting.date}T09:00:00`,
          end_datetime: meeting.date && endTime ? `${meeting.date}T${endTime}:00` : `${meeting.date}T10:00:00`,
          location: meeting.location || '',
          virtual_link: meeting.virtual_link || '',
          agenda: meeting.agenda_items.map(item => `${item.title}: ${item.description}`).join('\n') || '',
          status: 'draft'
        }

        console.log('Sending draft meeting data:', meetingData)
        await api.post('/meetings/', meetingData)
        
        showMeetingNotification('Draft Saved', meeting.title)
        
        router.push('/meetings')
      } catch (error) {
        console.error('Error saving draft:', error)
        if (error.response?.data?.errors) {
          showValidationErrors(error.response.data.errors)
        } else {
          showOperationError('save draft', error)
        }
      } finally {
        saving.value = false
      }
    }

    const cancelCreation = () => {
      router.go(-1)
    }

    // Load reference data
    const loadReferenceData = async () => {
      try {
        // Load meeting types
        const typesResponse = await api.get('/meetings/types')
        meetingTypes.value = typesResponse.data.meeting_types

        // Load board members
        const membersResponse = await api.get('/members')
        boardMembers.value = membersResponse.data.members || []
      } catch (error) {
        console.error('Error loading reference data:', error)
        // Use fallback data if API fails
        meetingTypes.value = [
          { id: 1, name: 'Regular Board Meeting', color: '#1565C0' },
          { id: 2, name: 'Emergency Meeting', color: '#F44336' },
          { id: 3, name: 'Committee Meeting', color: '#FF9800' },
          { id: 4, name: 'Strategic Planning', color: '#4CAF50' }
        ]
      }
    }

    // Initialize default values
    onMounted(async () => {
      // Set default date to tomorrow
      const tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + 1)
      meeting.date = tomorrow.toISOString().split('T')[0]
      
      // Set default start time
      meeting.start_time = '09:00'
      
      // Load reference data
      await loadReferenceData()
      
      // Set default meeting type after loading types
      if (meetingTypes.value.length > 0) {
        meeting.meeting_type_id = meetingTypes.value[0].id
      }
    })

    return {
      // Form refs
      meetingForm,
      formValid,
      saving,

      // Data
      meeting,
      meetingTypes,
      boardMembers,

      // Validation rules
      titleRules,
      typeRules,
      dateRules,
      timeRules,

      // Methods
      addAgendaItem,
      removeAgendaItem,
      saveMeeting,
      saveDraft,
      cancelCreation,
      loadReferenceData
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/variables.scss";

.create-meeting-page {
  .agenda-item {
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 8px;
    margin-bottom: 8px;
    padding: 16px;
  }

  .text-medium-emphasis {
    opacity: 0.7;
  }

  .bg-primary {
    background-color: rgb(var(--v-theme-primary)) !important;
  }
}
</style>